import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ShieldCheck, CreditCard, Truck, User, Lock, BadgeCheck, CheckCircle2, MapPin, PackageCheck, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatPrice, errText, imgUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { AddressForm, emptyAddress, addressComplete, buildShippingAddress } from "../components/AddressForm";
import { PaymentMethodIcons } from "../components/PaymentMethods";
import { Seo } from "../components/Seo";

const card = "bg-white border border-border rounded-2xl shadow-[0_1px_3px_rgba(0,0,0,0.04)]";
const input = "w-full rounded-xl border border-[#DADADA] bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-accent/60 focus:border-accent transition-colors";

function StepCard({ n, title, icon: Icon, done, children, testid }) {
  return (
    <div className={`${card} p-5 sm:p-6`} data-testid={testid}>
      <div className="flex items-center gap-3">
        <span className={`w-8 h-8 rounded-full text-sm font-bold flex items-center justify-center shrink-0 transition-colors ${done ? "bg-accent text-white" : "bg-foreground text-white"}`}>
          {done ? <CheckCircle2 className="w-4.5 h-4.5 w-5 h-5" /> : n}
        </span>
        <p className="font-bold text-[15px] flex items-center gap-2">{title}{Icon && <Icon className="w-4 h-4 text-accent" />}</p>
      </div>
      <div className="mt-4 pl-0 sm:pl-11">{children}</div>
    </div>
  );
}

export default function Checkout() {
  const { itemId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [guestMode, setGuestMode] = useState(false);
  const [guestEmail, setGuestEmail] = useState("");
  const [shipMethod, setShipMethod] = useState(null);
  const [addr, setAddr] = useState(emptyAddress);

  useEffect(() => {
    api.get(`/orders/checkout/preview/${itemId}`).then(({ data }) => {
      setPreview(data);
      setShipMethod(data.shipping_options?.[0]?.method || null);
    }).catch(() => setPreview(false));
  }, [itemId]);

  useEffect(() => {
    if (user?.name) {
      const parts = user.name.split(" ");
      setAddr((a) => ({ ...a, first_name: a.first_name || parts[0] || "", last_name: a.last_name || parts.slice(1).join(" ") || "",
        street: a.street || user.address?.street?.replace(/\s+\d.*$/, "") || "", zip: a.zip || user.address?.zip || "", city: a.city || user.address?.city || "" }));
    }
  }, [user]);

  if (preview === null) return <div className="max-w-6xl mx-auto px-4 py-14"><Seo noindex title="Kaufen" /><div className="h-64 bg-muted rounded-2xl animate-pulse" /></div>;
  if (preview === false) return <div className="max-w-3xl mx-auto px-4 py-20 text-center text-muted-foreground">Artikel ist nicht mehr verfügbar.</div>;

  const buyerReady = user || (guestMode && /\S+@\S+\.\S+/.test(guestEmail));
  const addrDone = addressComplete(addr);
  const canPay = buyerReady && addrDone && !busy;
  const nextUrl = encodeURIComponent(`/kaufen/${itemId}`);
  const deadlineDays = preview.shipping_deadline_days || 3;
  const shipFee = preview.shipping_options?.find((o) => o.method === shipMethod)?.price ?? preview.shipping_fee;
  const total = Math.round((preview.item_price + preview.buyer_fee + shipFee) * 100) / 100;

  const pay = async () => {
    if (!canPay) return;
    setBusy(true);
    try {
      const { data } = await api.post("/orders/checkout", {
        item_id: itemId, origin_url: window.location.origin,
        shipping_address: buildShippingAddress(addr),
        shipping_method: shipMethod,
        ...(user ? {} : { guest_email: guestEmail.trim(), guest_name: `${addr.first_name} ${addr.last_name}`.trim() }),
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(errText(err));
      setBusy(false);
    }
  };

  const summary = (
    <div className={`${card} overflow-hidden`} data-testid="checkout-summary">
      <div className="bg-[#111111] text-white px-5 py-4">
        <p className="text-[11px] font-bold tracking-[0.2em] uppercase text-accent">Deine Bestellung</p>
      </div>
      <div className="p-5">
        <div className="flex gap-4" data-testid="checkout-item-card">
          {preview.item.image_ids?.[0] && <img src={imgUrl(preview.item.image_ids[0], "thumb")} alt="" className="w-20 h-20 rounded-xl object-cover border border-border" />}
          <div className="min-w-0">
            <p className="font-semibold text-sm line-clamp-2" data-testid="checkout-item-title">{preview.item.title}</p>
            {preview.seller?.username && <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1"><BadgeCheck className="w-3.5 h-3.5 text-accent" /> Verkauft von @{preview.seller.username}</p>}
            {preview.negotiated && <p className="text-xs font-semibold text-accent mt-1" data-testid="negotiated-price-row">Verhandelter Preis · statt {formatPrice(preview.original_price)}</p>}
          </div>
        </div>
        <div className="mt-5 space-y-2.5 text-sm" data-testid="checkout-breakdown">
          <div className="flex justify-between"><span className="text-muted-foreground">Artikelpreis</span><span className="font-medium">{formatPrice(preview.item_price)}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Versand</span><span className="font-medium">{shipFee > 0 ? formatPrice(shipFee) : "Kostenlos"}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-accent" /> Käuferschutz</span><span className="font-medium">{formatPrice(preview.buyer_fee)}</span></div>
          <div className="flex justify-between items-baseline font-bold border-t border-dashed border-border pt-3 mt-3">
            <span>Gesamt</span><span className="text-xl" data-testid="checkout-total">{formatPrice(total)}</span>
          </div>
          <p className="text-[11px] text-muted-foreground text-right -mt-1">inkl. aller Gebühren</p>
        </div>
        <button data-testid="checkout-pay-btn" onClick={pay} disabled={!canPay}
          className="hidden md:block w-full mt-5 bg-accent text-white font-bold py-4 rounded-full hover:opacity-90 active:scale-[0.98] transition-transform disabled:opacity-40">
          {busy ? "Weiterleitung zur Zahlung…" : `Jetzt sicher bezahlen · ${formatPrice(total)}`}
        </button>
        {!canPay && !busy && (
          <p className="hidden md:block text-[11px] text-muted-foreground text-center mt-2">
            {!buyerReady ? "Bitte wähle oben, wie du bestellen möchtest." : "Bitte vervollständige deine Lieferangaben."}
          </p>
        )}
        <div className="mt-4 flex justify-center"><PaymentMethodIcons methods={preview.payment_methods} /></div>
        <p className="hidden md:block text-[11px] text-muted-foreground text-center mt-3" data-testid="checkout-legal-links">
          Mit dem Kauf akzeptierst du die <Link to="/agb" className="underline hover:text-accent">AGB</Link> (inkl. Widerrufsrecht). Es gilt der <Link to="/kaeuferschutz" className="underline hover:text-accent">Käuferschutz</Link>.
        </p>
      </div>
      <div className="border-t border-dashed border-border bg-[#FAF9F5] px-5 py-3.5 grid grid-cols-3 gap-2 text-center">
        {[[Lock, "SSL-verschlüsselt"], [ShieldCheck, "Käuferschutz"], [Undo2, "Geld-zurück bei Problemen"]].map(([Icon, t]) => (
          <span key={t} className="text-[10px] font-semibold text-stone-600 flex flex-col items-center gap-1"><Icon className="w-4 h-4 text-accent" /> {t}</span>
        ))}
      </div>
    </div>
  );

  return (
    <div className="bg-[#FAFAF8] min-h-screen">
      <Seo noindex title="Kasse" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 pb-32 md:pb-14">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-4xl font-bold" data-testid="checkout-heading">Kasse</h1>
            <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> Sichere Zahlung über KLEDIO & Stripe</p>
          </div>
          <div className="hidden sm:flex items-center gap-2 text-[11px] font-bold" data-testid="checkout-progress">
            <span className="flex items-center gap-1.5 text-accent"><span className="w-5 h-5 rounded-full bg-accent text-white flex items-center justify-center text-[10px]">1</span> Lieferung</span>
            <span className="w-8 h-px bg-border" />
            <span className="flex items-center gap-1.5 text-muted-foreground"><span className="w-5 h-5 rounded-full border border-border flex items-center justify-center text-[10px]">2</span> Bezahlen</span>
            <span className="w-8 h-px bg-border" />
            <span className="flex items-center gap-1.5 text-muted-foreground"><span className="w-5 h-5 rounded-full border border-border flex items-center justify-center text-[10px]">3</span> Bestätigung</span>
          </div>
        </div>

        <div className="mt-7 grid lg:grid-cols-[1fr_400px] gap-6 items-start">
          <div className="space-y-4">
            <StepCard n={1} title={user ? "Dein Konto" : "Wie möchtest du bestellen?"} icon={User} done={!!buyerReady} testid="checkout-buyer-status">
              {user ? (
                <p className="text-sm">Eingeloggt als <span className="font-bold">{user.email}</span></p>
              ) : (
                <>
                  <div className="grid sm:grid-cols-3 gap-2">
                    <button data-testid="checkout-login-btn" onClick={() => nav(`/anmelden?next=${nextUrl}`)}
                      className="border border-border rounded-xl py-3 text-sm font-semibold hover:border-foreground transition-colors">Einloggen</button>
                    <button data-testid="checkout-register-btn" onClick={() => nav(`/registrieren?next=${nextUrl}`)}
                      className="border border-border rounded-xl py-3 text-sm font-semibold hover:border-foreground transition-colors">Registrieren</button>
                    <button data-testid="checkout-guest-btn" onClick={() => setGuestMode(true)}
                      className={`rounded-xl py-3 text-sm font-semibold transition-colors ${guestMode ? "bg-accent text-white" : "border border-border hover:border-accent hover:text-accent"}`}>
                      Als Gast kaufen
                    </button>
                  </div>
                  {guestMode && (
                    <div className="mt-4">
                      <label className="text-xs font-semibold block mb-1.5">E-Mail-Adresse (für deine Bestellbestätigung)</label>
                      <input data-testid="checkout-guest-email" className={input} type="email" placeholder="deine@email.de"
                        value={guestEmail} onChange={(e) => setGuestEmail(e.target.value)} required />
                    </div>
                  )}
                </>
              )}
            </StepCard>

            <StepCard n={2} title="Lieferung" icon={MapPin} done={addrDone} testid="checkout-address-card">
              <AddressForm value={addr} onChange={setAddr} packstationEnabled={preview.packstation_enabled !== false} />
            </StepCard>

            <StepCard n={3} title="Versand" icon={Truck} done={!!shipMethod} testid="checkout-shipping-card">
              <div className="space-y-2">
                {preview.shipping_options?.map((o) => (
                  <label key={o.method} data-testid={`shipping-option-${o.method}`}
                    className={`flex items-center justify-between border rounded-xl px-4 py-3 cursor-pointer transition-colors ${shipMethod === o.method ? "border-accent bg-accent/5" : "border-border hover:border-foreground/30"}`}>
                    <span className="flex items-center gap-3 text-sm">
                      <input type="radio" name="ship" className="accent-[#FF6B35]" checked={shipMethod === o.method} onChange={() => setShipMethod(o.method)} />
                      {o.label}
                    </span>
                    <span className="text-sm font-semibold">{o.price > 0 ? formatPrice(o.price) : "Kostenlos"}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1.5" data-testid="checkout-deadline-note">
                <PackageCheck className="w-3.5 h-3.5 text-accent shrink-0" /> Der Verkäufer versendet innerhalb von {deadlineDays} Kalendertagen nach Zahlungseingang — mit Sendungsnummer zum Mitverfolgen.
              </p>
            </StepCard>

            <StepCard n={4} title="Zahlungsart" icon={CreditCard} done={false} testid="checkout-payment-card">
              <PaymentMethodIcons methods={preview.payment_methods} />
              <p className="text-xs text-muted-foreground mt-3">Du wählst deine Zahlungsart im nächsten Schritt auf unserer sicheren Bezahlseite aus. Die Zahlungsabwicklung erfolgt verschlüsselt über Stripe — KLEDIO speichert keine Zahlungsdaten.</p>
            </StepCard>

            <div className={`${card} p-5 flex gap-3`} data-testid="checkout-protection-card">
              <span className="w-10 h-10 rounded-xl bg-accent/10 text-accent flex items-center justify-center shrink-0"><ShieldCheck className="w-5 h-5" /></span>
              <div>
                <p className="font-bold text-sm">KLEDIO Käuferschutz inklusive</p>
                <p className="text-sm text-muted-foreground mt-1">Dein Geld wird erst nach Zustellung und Ablauf der Schutzfrist an den Verkäufer ausgezahlt. Kommt der Artikel nicht an oder weicht er stark von der Beschreibung ab, bekommst du dein Geld zurück.</p>
                <Link to="/kaeuferschutz" className="text-sm font-semibold text-accent hover:underline mt-1 inline-block">Wie der Käuferschutz funktioniert</Link>
              </div>
            </div>
          </div>

          <div className="lg:sticky lg:top-24">{summary}</div>
        </div>
      </div>

      {/* Mobile sticky CTA */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-border shadow-[0_-4px_20px_rgba(0,0,0,0.06)] px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
        <button data-testid="checkout-pay-btn-mobile" onClick={pay} disabled={!canPay}
          className="w-full bg-accent text-white font-bold py-3.5 rounded-full active:scale-[0.98] transition-transform disabled:opacity-40">
          {busy ? "Weiterleitung…" : `Jetzt sicher bezahlen · ${formatPrice(total)}`}
        </button>
        <p className="text-[10px] text-muted-foreground text-center mt-1.5">
          Es gelten <Link to="/agb" className="underline">AGB</Link> (inkl. Widerruf) & <Link to="/kaeuferschutz" className="underline">Käuferschutz</Link>.
        </p>
      </div>
    </div>
  );
}

export function PaymentSuccess() {
  const [status, setStatus] = useState("checking");
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    const sessionId = new URLSearchParams(window.location.search).get("session_id");
    if (!sessionId) return setStatus("error");
    let attempts = 0;
    const poll = async () => {
      if (attempts++ > 10) return setStatus("timeout");
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") return setStatus("paid");
        if (data.status === "expired" || data.payment_status === "failed") return setStatus("error");
      } catch {}
      setTimeout(poll, 2000);
    };
    poll();
  }, []);

  return (
    <div className="max-w-md mx-auto px-4 py-20 text-center">
      {status === "checking" && <><div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" /><p className="mt-6 font-semibold" data-testid="payment-checking">Zahlung wird bestätigt…</p></>}
      {status === "paid" && (
        <div data-testid="payment-success">
          <div className="w-16 h-16 bg-accent text-white rounded-full flex items-center justify-center mx-auto text-2xl">✓</div>
          <h1 className="text-2xl sm:text-4xl font-bold mt-6">Zahlung erfolgreich!</h1>
          <p className="text-muted-foreground text-sm mt-3">Der Verkäufer wurde informiert und versendet deinen Artikel in Kürze.{!user && " Deine Bestellbestätigung erhältst du per E-Mail."}</p>
          {user ? (
            <button data-testid="goto-orders-btn" onClick={() => nav("/bestellungen")} className="mt-8 bg-primary text-primary-foreground font-bold px-8 py-3.5 rounded-full hover:opacity-90">Zu meinen Bestellungen</button>
          ) : (
            <button data-testid="goto-home-btn" onClick={() => nav("/")} className="mt-8 bg-primary text-primary-foreground font-bold px-8 py-3.5 rounded-full hover:opacity-90">Zurück zum Marktplatz</button>
          )}
        </div>
      )}
      {(status === "error" || status === "timeout") && (
        <div data-testid="payment-error">
          <h1 className="text-2xl sm:text-4xl font-bold">Hmm…</h1>
          <p className="text-muted-foreground text-sm mt-3">{status === "timeout" ? "Die Bestätigung dauert länger als erwartet. Prüfe deine Bestellungen in ein paar Minuten." : "Die Zahlung konnte nicht bestätigt werden."}</p>
          <button onClick={() => nav(user ? "/bestellungen" : "/")} className="mt-8 border border-border font-bold px-8 py-3.5 rounded-full hover:bg-secondary">{user ? "Zu meinen Bestellungen" : "Zurück zum Marktplatz"}</button>
        </div>
      )}
    </div>
  );
}

export function PaymentCancel() {
  const nav = useNavigate();
  return (
    <div className="max-w-md mx-auto px-4 py-20 text-center" data-testid="payment-cancel">
      <h1 className="text-2xl sm:text-4xl font-bold">Zahlung abgebrochen</h1>
      <p className="text-muted-foreground text-sm mt-3">Keine Sorge — es wurde nichts abgebucht. Der Artikel wird in Kürze wieder freigegeben.</p>
      <button onClick={() => nav("/")} className="mt-8 border border-border font-bold px-8 py-3.5 rounded-full hover:bg-secondary">Zurück zum Marktplatz</button>
    </div>
  );
}
